import React, { useState, useRef } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';
import { PDFDocument } from 'pdf-lib';
import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Render scale for rasterizing each page before OCR — higher = better accuracy.
const RENDER_SCALE = 2.0;

// The OCR engine (tesseract.js core wasm + worker + eng language data) is
// self-hosted under public/tessdata/ so nothing is fetched from an external
// CDN at runtime. Build an absolute base URL (tesseract requires absolute).
const tessRoot = () => new URL(`${process.env.PUBLIC_URL || ''}/tessdata/`, document.baseURI).href;

const PdfOcr = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [submitValue, setSubmitValue] = useState('Make Searchable');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setFile(null);
    setSubmitValue('Make Searchable');
    setNotify('');
    setError(null);
    setProcessing(false);
    setProgress(0);
    setStatusText('');
    setDone(false);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleFileChange = (event) => {
    const selected = event.target.files[0];
    if (!selected) return;
    if (selected.type !== constants.pdfFileType) {
      setNotify(constants.onlyPdfAllowed);
      setError('error');
      return;
    }
    setFile(selected);
    setNotify('');
    setError(null);
    setDone(false);
  };

  const runOcr = async () => {
    if (!file) return;
    setProcessing(true);
    setSubmitValue(<Spinner />);
    setError(null);
    setNotify('');
    setProgress(0);
    setStatusText('Loading OCR engine…');

    let worker;
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfJsDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const numPages = pdfJsDoc.numPages;

      const root = tessRoot();
      worker = await createWorker('eng', 1, {
        workerPath: new URL('worker.min.js', root).href,
        corePath: root,
        langPath: root,
        cacheMethod: 'none',
        gzip: true,
        logger: (m) => {
          if (m && m.status === 'recognizing text' && typeof m.progress === 'number') {
            setStatusText(`Recognizing text… ${Math.round(m.progress * 100)}%`);
          }
        },
      });

      const outDoc = await PDFDocument.create();

      for (let i = 1; i <= numPages; i++) {
        setStatusText(`OCR page ${i} of ${numPages}…`);
        setProgress(Math.round(((i - 1) / numPages) * 100));

        // Rasterize the page (white background helps OCR on scans).
        const pdfJsPage = await pdfJsDoc.getPage(i);
        const viewport = pdfJsPage.getViewport({ scale: RENDER_SCALE });
        let canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await pdfJsPage.render({ canvasContext: ctx, viewport }).promise;

        // OCR -> a one-page searchable PDF (image + invisible text layer).
        const { data } = await worker.recognize(canvas, {}, { pdf: true });
        const pageBytes = new Uint8Array(data.pdf);

        // Append that page into the merged output document.
        const srcDoc = await PDFDocument.load(pageBytes);
        const [copied] = await outDoc.copyPages(srcDoc, [0]);
        outDoc.addPage(copied);

        // Free the canvas before the next page.
        canvas.width = 0;
        canvas.height = 0;
        canvas = null;
      }

      setProgress(100);
      setStatusText('Finishing…');

      const pdfBytes = await outDoc.save();
      const blob = new Blob([pdfBytes], { type: constants.pdfFileType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `searchable_${file.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addUsage('AWESOME PDF TOOLS PdfOcr', '1.0', numPages, window.location.href);
      setDone(true);
      setSubmitValue('Make Searchable');
    } catch (err) {
      console.error('OCR failed:', err);
      setNotify('OCR failed. The PDF may be encrypted or the page is too complex. Please try again.');
      setError('error');
      setSubmitValue('Make Searchable');
    } finally {
      setProcessing(false);
      if (worker) {
        try {
          await worker.terminate();
        } catch (e) {
          /* ignore */
        }
      }
    }
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF9900">
            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.5h8v1.5H8v-1.5zm0-3h8v1.5H8V12zm0-3h5v1.5H8V9z" />
          </svg>
        </div>
        <h3 className="service-card-title">PDF OCR</h3>
        <p className="service-card-desc">Turn scanned PDFs into searchable, selectable text documents</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              {!done && (
                <Button variant="primary" disabled={!file || processing || error} onClick={runOcr}>
                  {submitValue}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="PDF OCR — Make Scanned PDFs Searchable"
      >
        <div>
          <div className="bordered-box">
            <h6 className="container-header">📄 Choose Scanned PDF</h6>
            <FormField label="Upload a PDF to make its text searchable and selectable" errorText={notify}>
              <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={handleFileChange} />
            </FormField>
            {file && !processing && !done && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#52c41a' }}>📄 {file.name}</div>
            )}
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#565959' }}>
              Runs entirely in your browser (English). Large or scanned PDFs can take a while — each page is read by
              the OCR engine, then a searchable text layer is added on top of the original page image.
            </div>
          </div>

          {processing && (
            <div className="bordered-box">
              <SpaceBetween size="m">
                <ProgressBar value={progress} label="Recognizing text" description={statusText} />
                <Box textAlign="center">
                  <Spinner /> <span style={{ fontSize: '13px', color: '#565959' }}>{statusText}</span>
                </Box>
              </SpaceBetween>
            </div>
          )}

          {done && (
            <div className="bordered-box" style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontSize: '30px', marginBottom: '8px' }}>✅</div>
              <h3 style={{ margin: '0 0 8px', color: '#52c41a' }}>Searchable PDF Ready!</h3>
              <p style={{ color: '#666', marginBottom: '16px' }}>
                Your searchable PDF has downloaded. The text is now selectable and searchable.
              </p>
              <Button variant="link" onClick={resetState}>
                OCR another PDF
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PdfOcr;
